import { LinkOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client/react';
import { css } from '@emotion/css';
import { antdTokenFilter, matchesTokens, tokenize } from '@medcom/shared-ui/utils/tokenSearch.ts';
import {
  Button,
  Checkbox,
  Col,
  Form,
  type FormInstance,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Tabs,
  Tag,
  Transfer,
  Typography,
  theme,
} from 'antd';
import React from 'react';
import { EditableTagGroup } from '../../../../components/EditableTagGroup.tsx';
import { LoadingIndicator } from '../../../../components/LoadingIndicator.tsx';
import { ParameterInsuranceEditor } from '../../../../components/ParameterInsuranceEditor';
import { type Parameter, ParameterSelect } from '../../../../components/ParameterSelect';
import { SpecimenSelect } from '../../../../components/SpecimenSelect';
import { type InsuranceDefinitionInput, PartnerLab } from '../../../../graphql/generated/graphql.ts';
import { translatePartnerLab } from '../../../../utils/enumHelpers';
import { PARAMETER_FORM_QUERY } from './ParameterForm.gql';

const { useToken } = theme;

export interface InsuranceDefinitionAware {
  insuranceDefinitions: InsuranceDefinitionInput[];
}

interface Props<T> {
  form: FormInstance<T>;
  initialValues?: T;
  onFinish: (values: T) => void;
  mode: 'create' | 'update';
  parameters: Parameter[];
  labId: string;
  shortNameValidator?: (shortName: string | null | undefined) => boolean;
  edifactNumberValidator: (edifactNumber: number | null | undefined) => boolean;
}

export const ParameterForm = <T extends InsuranceDefinitionAware & { specimenIds: string[] }>({
  form,
  initialValues,
  onFinish,
  mode,
  parameters,
  labId,
  shortNameValidator,
  edifactNumberValidator,
}: Props<T>) => {
  const { token } = useToken();
  const { data, loading } = useQuery(PARAMETER_FORM_QUERY, {
    variables: {
      labId: labId,
    },
    skip: !labId,
  });

  const selectedSpecimenIds = Form.useWatch('specimenIds', form);

  const stationsContainer = css`
    margin-top: -${token.margin}px;
    margin-bottom: ${token.margin}px;
  `;

  const specimenStationGroup = css`
    margin-bottom: ${token.marginXS}px;
  `;

  const specimenStationText = css`
    font-size: ${token.fontSizeSM}px;
    display: block;
  `;

  const stationTag = css`
    margin-bottom: ${token.marginXXS}px;
    margin-right: ${token.marginXXS}px;
  `;

  // Group analysis stations by specimen
  const stationsBySpecimen = React.useMemo(() => {
    if (!selectedSpecimenIds || selectedSpecimenIds.length === 0 || !data?.labSpecimens) {
      return [];
    }

    const selectedSpecimens = data.labSpecimens.filter(s => selectedSpecimenIds.includes(s.id));

    return selectedSpecimens
      .filter(specimen => specimen.analysisStations.length > 0)
      .map(specimen => ({
        specimenName: specimen.name,
        stations: specimen.analysisStations,
      }));
  }, [selectedSpecimenIds, data?.labSpecimens]);

  if (loading) {
    return <LoadingIndicator height="200px" />;
  }

  return (
    <Form<T> layout="vertical" form={form} onFinish={onFinish} initialValues={initialValues}>
      <Tabs
        defaultActiveKey="grunddaten"
        size="small"
        items={[
          {
            key: 'grunddaten',
            label: 'Grunddaten',
            forceRender: true,
            children: (
              <>
                <Row gutter={[16, 0]}>
                  <Col span={5}>
                    <Form.Item
                      name="shortName"
                      label="Kurzbezeichnung"
                      rules={[
                        { type: 'string', required: mode === 'create', whitespace: true },
                        {
                          validator: (_, shortName) =>
                            !shortNameValidator || shortNameValidator(shortName)
                              ? Promise.resolve()
                              : Promise.reject('Kurzbezeichnung bereits vergeben'),
                        },
                      ]}
                    >
                      <Input
                        autoFocus={mode === 'create'}
                        disabled={mode === 'update'}
                        placeholder={mode === 'update' ? 'nicht änderbar' : ''}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={11}>
                    <Form.Item name="longName" label="Langbezeichnung" rules={[{ type: 'string', required: true, whitespace: true }]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item
                      name="edifactNumber"
                      label="Edifact-Nummer"
                      rules={[
                        { type: 'integer', required: false, min: 1, max: 9999999, message: 'Bitte Zahl angeben' },
                        {
                          validator: (_, editfactNumber) =>
                            edifactNumberValidator(editfactNumber) ? Promise.resolve() : Promise.reject('Edifact-Nummer bereits vergeben'),
                        },
                      ]}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        inputMode="numeric"
                        precision={0}
                        max={9999999}
                        min={1}
                        step={1}
                        autoFocus={mode === 'update'}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Form.Item
                      name="loincCode"
                      label="LOINC Code"
                      tooltip="Standardisierter Code zur Parameteridentifikation. Wird als Kontext für den KI-Assistenten verwendet."
                    >
                      <Input placeholder="z.B. 2093-3" />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[16, 0]}>
                  <Col span={18}>
                    <Form.Item name="synonyms" label="Synonyme" valuePropName="tags" rules={[{ type: 'array' }]}>
                      <EditableTagGroup addLabel="Neues Synonym" inputWidth="120px" />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Space orientation="vertical">
                      <Form.Item name="deactivated" valuePropName="checked" noStyle>
                        <Checkbox>Deaktiviert</Checkbox>
                      </Form.Item>
                      <Form.Item name="acute" valuePropName="checked" noStyle>
                        <Checkbox>Akut anforderbar</Checkbox>
                      </Form.Item>
                    </Space>
                  </Col>
                </Row>
                <Row gutter={[16, 0]}>
                  <Col span={12}>
                    <Form.Item name="specimenIds" label="Material" valuePropName="selected">
                      <SpecimenSelect style={{ width: '100%' }} multiple specimens={data?.labSpecimens ?? []} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="analysisStationIds" label="Analysestationen">
                      <Select
                        showSearch={{ filterOption: antdTokenFilter }}
                        allowClear
                        mode="multiple"
                        placeholder="Suche nach Analysestationen"
                        options={[...(data?.labAnalysisStations ?? [])]
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map(it => ({
                            label: it.name,
                            value: it.id,
                            search: it.name,
                          }))}
                      />
                    </Form.Item>
                    {stationsBySpecimen.length > 0 && (
                      <div className={stationsContainer}>
                        {stationsBySpecimen.map((group, index) => (
                          <div key={index} className={specimenStationGroup}>
                            <Typography.Text type="secondary" className={specimenStationText}>
                              {group.specimenName}:{' '}
                              {group.stations.map(station => (
                                <Tag key={station.id} className={stationTag}>
                                  {station.name}
                                </Tag>
                              ))}
                            </Typography.Text>
                          </div>
                        ))}
                      </div>
                    )}
                  </Col>
                </Row>
                <Row gutter={[16, 0]}>
                  <Col span={24}>
                    <Form.Item name="formIds" label="Formulare">
                      <Select
                        showSearch={{ filterOption: antdTokenFilter }}
                        allowClear
                        mode="multiple"
                        placeholder="Suche nach Formularen"
                        options={[...(data?.labForms ?? [])]
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map(it => ({
                            label: it.name,
                            value: it.id,
                            search: it.name,
                          }))}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[16, 0]}>
                  <Col span={12}>
                    <Form.Item name="withParameterIds" label="Nur mit" valuePropName="selected">
                      <ParameterSelect style={{ width: '100%' }} multiple parameters={parameters} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="withoutParameterIds" label="Nicht mit" valuePropName="selected">
                      <ParameterSelect style={{ width: '100%' }} multiple parameters={parameters} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[16, 0]}>
                  <Col span={12}>
                    <Form.Item name="partnerLab" label="Partnerlabor" rules={[{ type: 'enum', enum: Object.keys(PartnerLab) }]}>
                      <Select>
                        {Object.keys(PartnerLab).map(it => {
                          return (
                            <Select.Option key={it} value={it}>
                              {translatePartnerLab(it, data?.lab)}
                            </Select.Option>
                          );
                        })}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="volume"
                      label="Volumen"
                      rules={[{ type: 'integer', required: true, min: 0, max: 9999999, message: 'Bitte Zahl angeben' }]}
                    >
                      <InputNumber inputMode="numeric" required min={0} max={9999999} step={1} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[16, 0]}>
                  <Col span={12}>
                    <Form.Item
                      name="storagePeriod"
                      label="Lagerdauer"
                      tooltip="-1 = Keine Lagerdauer von Material überschreiben"
                      rules={[{ type: 'integer', required: true, min: -1, max: 1000, message: 'Bitte Zahl angeben' }]}
                    >
                      <InputNumber inputMode="numeric" required min={-1} max={1000} step={1} suffix="Stunden" style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="evalHours"
                      label="Auswertungsdauer"
                      tooltip="-1 = Keine Auswertungsdauer"
                      rules={[{ type: 'integer', required: true, min: -1, max: 10000, message: 'Bitte Zahl angeben' }]}
                    >
                      <InputNumber inputMode="numeric" required min={-1} max={10000} step={1} suffix="Stunden" style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[16, 0]}>
                  <Col span={6}>
                    <Form.Item name="doctorBilling" valuePropName="checked">
                      <Checkbox>Privat an Zuweiser</Checkbox>
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item name="patientBilling" valuePropName="checked">
                      <Checkbox>Privat an Patient</Checkbox>
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item name="lgDoctorBilling" valuePropName="checked">
                      <Checkbox>LG - Privat an Zuweiser</Checkbox>
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={[16, 0]}>
                  <Col span={6}>
                    <Form.Item
                      name="priceDoctor"
                      label="Preis Zuweiser"
                      rules={[
                        {
                          type: 'number',
                          required: false,
                          min: 0,
                          max: 9999.99,
                          message: 'Bitte validen Betrag angeben',
                        },
                      ]}
                    >
                      <InputNumber
                        inputMode="decimal"
                        precision={2}
                        decimalSeparator=","
                        min={0}
                        max={9999.99}
                        step={0.01}
                        suffix="€"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name="pricePatient"
                      label="Preis Patient"
                      rules={[
                        {
                          type: 'number',
                          required: false,
                          min: 0,
                          max: 9999.99,
                          message: 'Bitte validen Betrag angeben',
                        },
                      ]}
                    >
                      <InputNumber
                        inputMode="decimal"
                        precision={2}
                        decimalSeparator=","
                        min={0}
                        max={9999.99}
                        step={0.01}
                        suffix="€"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name="priceLgDoctor"
                      label="Preis LG-Zuweiser"
                      rules={[
                        {
                          type: 'number',
                          required: false,
                          min: 0,
                          max: 9999.99,
                          message: 'Bitte validen Betrag angeben',
                        },
                      ]}
                    >
                      <InputNumber
                        inputMode="decimal"
                        precision={2}
                        decimalSeparator=","
                        min={0}
                        max={9999.99}
                        step={0.01}
                        suffix="€"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name="priceDeductible"
                      label="Preis Selbstbehalt"
                      rules={[
                        {
                          type: 'number',
                          required: false,
                          min: 0,
                          max: 9999.99,
                          message: 'Bitte validen Betrag angeben',
                        },
                      ]}
                    >
                      <InputNumber
                        inputMode="decimal"
                        precision={2}
                        decimalSeparator=","
                        min={0}
                        max={9999.99}
                        step={0.01}
                        suffix="€"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            ),
          },
          {
            key: 'krankenkassen',
            label: 'Krankenkassen',
            forceRender: true,
            children: (
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, curValues) => prevValues.insuranceDefinitions !== curValues.insuranceDefinitions}
              >
                {() => {
                  return (
                    <ParameterInsuranceEditor
                      periodicGroups={data?.labPeriodicGroups ?? []}
                      insurances={data?.insurances ?? []}
                      diagnoses={data?.labDiagnoses ?? []}
                      lg={false}
                    />
                  );
                }}
              </Form.Item>
            ),
          },
          {
            key: 'lg-krankenkassen',
            label: 'LG Krankenkassen',
            forceRender: true,
            children: (
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, curValues) => prevValues.insuranceDefinitions !== curValues.insuranceDefinitions}
              >
                {() => (
                  <ParameterInsuranceEditor
                    periodicGroups={data?.labPeriodicGroups ?? []}
                    insurances={data?.insurances ?? []}
                    diagnoses={data?.labDiagnoses ?? []}
                    lg={true}
                  />
                )}
              </Form.Item>
            ),
          },
          {
            key: 'sondertarife',
            label: 'Sondertarife',
            forceRender: true,
            children: (
              <Form.Item name="specialRateIds" valuePropName="targetKeys">
                <Transfer
                  dataSource={[...(data?.labSpecialRates ?? [])].sort((a, b) => a.shortName.localeCompare(b.shortName))}
                  titles={['Nicht anforderbar', 'Anforderbar']}
                  render={sr => (
                    <>
                      {sr.shortName} <Typography.Text type="secondary">{sr.code}</Typography.Text>
                    </>
                  )}
                  showSearch
                  filterOption={(inputValue, specialRate) =>
                    matchesTokens(specialRate, tokenize(inputValue), [sr => sr.name, sr => sr.shortName])
                  }
                  styles={{ section: { height: '300px', width: '100%', overflow: 'hidden' } }}
                  rowKey={sr => sr.id}
                />
              </Form.Item>
            ),
          },
          {
            key: 'informationen',
            label: 'Informationen',
            forceRender: true,
            children: (
              <>
                <Form.Item name="link" label="Link" rules={[{ type: 'url' }]}>
                  <Input prefix={<LinkOutlined />} />
                </Form.Item>
                <Form.Item
                  name="importantDescription"
                  label="Wichtige Informationen"
                  tooltip="Anzeige in Parameter-Info-Dialog, den ausgewählten Parametern und Zusammenfassung"
                >
                  <Input.TextArea rows={5} />
                </Form.Item>
                <Form.Item name="description" label="Beschreibung" tooltip="Anzeige in Parameter-Info-Dialog">
                  <Input.TextArea rows={5} />
                </Form.Item>
                <Form.Item
                  name="billingDescription"
                  label="Verrechnungsinformationen"
                  tooltip="Anzeige in Parameter-Info-Dialog und Verrechnungsdialog"
                >
                  <Input.TextArea rows={5} />
                </Form.Item>
                <Form.Item
                  name="aiDescription"
                  label="KI-Beschreibung"
                  tooltip="Zusätzlicher Kontext für den KI-Assistenten, z.B. wann dieser Parameter typischerweise angefordert wird"
                >
                  <Input.TextArea rows={3} />
                </Form.Item>
              </>
            ),
          },
        ]}
      />
      {/* hack for enter button support */}
      <Button htmlType="submit" hidden />
    </Form>
  );
};
